/**
 * OAuth callback handler. Receives ?code=&state=  OR  error=...&state=...
 * Exchanges the code for a token and redirects to the new sub-merchant detail page.
 */
import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle, XCircle, Spinner, ArrowRight } from "@phosphor-icons/react";

export default function OAuthCallback() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const code = params.get("code");
  const state = params.get("state");
  const error = params.get("error");
  const errDesc = params.get("error_description");

  const [status, setStatus] = useState(error ? "denied" : "exchanging");
  const [errMsg, setErrMsg] = useState(errDesc || "");
  const [tokenResp, setTokenResp] = useState(null);
  const [submerchantId, setSubmerchantId] = useState(null);
  const [apps, setApps] = useState([]);
  const [chosenClientId, setChosenClientId] = useState("");
  const [chosenClientSecret, setChosenClientSecret] = useState("");
  const [redirectUri, setRedirectUri] = useState(window.location.origin + "/oauth/callback");

  // Find the client_id this code was issued to (we only know after token exchange).
  // Strategy: try each of our clients with the code+secret until one works.
  useEffect(() => {
    if (error || !code) return;
    api.get("/applications").then(r => {
      setApps(r.data);
      // try each client
      tryExchange(r.data);
    });
    // eslint-disable-next-line
  }, []);

  const tryExchange = async (allApps) => {
    for (const app of allApps) {
      for (const c of (app.clients || [])) {
        for (const ru of (c.redirect_uris || [])) {
          try {
            const { data } = await api.post("/oauth/token", {
              client_id: c.client_id,
              client_secret: c.client_secret,
              grant_type: "authorization_code",
              redirect_uri: ru,
              code,
              mode: c.env === "production" ? "live" : "test",
            });
            // Find the sub-merchant we just created
            const list = await api.get("/submerchants");
            const sm = list.data.find(s => s.razorpay_account_id === data.razorpay_account_id);
            setTokenResp(data);
            setSubmerchantId(sm?.id);
            setChosenClientId(c.client_id);
            setChosenClientSecret(c.client_secret);
            setRedirectUri(ru);
            setStatus("success");
            return;
          } catch (_) { /* try next */ }
        }
      }
    }
    setStatus("error");
    setErrMsg("No matching client could exchange this code. It may have been used or expired.");
  };

  if (status === "denied") {
    return (
      <Wrap>
        <div className="text-center">
          <XCircle weight="fill" size={42} className="mx-auto text-red-500" />
          <h1 className="mt-3 font-display text-2xl font-bold">Authorization {error.replace(/_/g, " ")}</h1>
          <p className="mt-1 text-sm text-slate-500" data-testid="callback-error">{errMsg || "The sub-merchant denied the request."}</p>
          <p className="mt-2 text-xs text-slate-500 font-mono">state: {state}</p>
          <Link to="/dashboard" className="mt-5 inline-flex items-center gap-2 btn-primary rounded-md px-4 py-2 text-sm font-medium">
            Back to dashboard
          </Link>
        </div>
      </Wrap>
    );
  }

  if (status === "exchanging") {
    return (
      <Wrap>
        <div className="text-center">
          <Spinner size={36} className="mx-auto animate-spin text-[#FF6B00]" />
          <h1 className="mt-3 font-display text-xl font-bold">Exchanging authorization code…</h1>
          <p className="mt-1 text-sm text-slate-500">Generating access & refresh tokens.</p>
        </div>
      </Wrap>
    );
  }

  if (status === "error") {
    return (
      <Wrap>
        <div className="text-center">
          <XCircle weight="fill" size={42} className="mx-auto text-red-500" />
          <h1 className="mt-3 font-display text-2xl font-bold">Token exchange failed</h1>
          <p className="mt-1 text-sm text-slate-500" data-testid="callback-error">{errMsg}</p>
        </div>
      </Wrap>
    );
  }

  // success
  return (
    <Wrap maxWide>
      <div>
        <div className="flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#D1FAE5]">
            <CheckCircle weight="fill" size={26} className="text-[#10B981]" />
          </div>
        </div>
        <h1 className="mt-4 text-center font-display text-2xl font-bold">Sub-merchant connected</h1>
        <p className="mt-1 text-center text-sm text-slate-500">Razorpay returned <code className="font-mono">code</code> + <code className="font-mono">state</code>; we exchanged for tokens.</p>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs" data-testid="callback-token-response">
          <KV label="razorpay_account_id" value={tokenResp.razorpay_account_id} />
          <KV label="public_token" value={tokenResp.public_token} />
          <KV label="token_type" value={tokenResp.token_type} />
          <KV label="expires_in (sec)" value={tokenResp.expires_in.toString()} />
          <KV label="access_token (preview)" value={tokenResp.access_token.slice(0, 40) + "…"} />
          <KV label="refresh_token (preview)" value={tokenResp.refresh_token.slice(0, 40) + "…"} />
        </div>

        <div className="mt-6 flex items-center justify-center gap-3">
          <Link to="/submerchants" className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm hover:bg-slate-50" data-testid="callback-view-list">View all sub-merchants</Link>
          {submerchantId && (
            <Link to={`/submerchants/${submerchantId}`} className="btn-primary inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium" data-testid="callback-view-submerchant">
              Go to sub-merchant <ArrowRight size={12} weight="bold" />
            </Link>
          )}
        </div>
      </div>
    </Wrap>
  );
}

function Wrap({ children, maxWide }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 grid-bg px-4 py-10">
      <div className={`w-full ${maxWide ? "max-w-2xl" : "max-w-md"} rounded-2xl border border-[#E2E8F0] bg-white p-8 shadow-sm`}>
        {children}
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="rounded-md bg-slate-50 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className="mt-0.5 font-mono text-xs text-slate-800 break-all">{value}</div>
    </div>
  );
}
